import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { isHelpdeskMode } from '../../utils/helpdeskMode';
import {
  Box,
  Typography,
  Paper,
  Chip,
  IconButton,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Snackbar,
  InputAdornment,
  Tooltip,
  Pagination,
  Checkbox,
  Divider,
  alpha,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  ViewList as ListViewIcon,
  ViewModule as CardViewIcon,
  PriorityHigh as UrgentIcon,
  KeyboardArrowUp as HighIcon,
  Remove as MediumIcon,
  KeyboardArrowDown as LowIcon,
  FiberManualRecord as DotIcon,
  AccessTime as ClockIcon,
  Person as PersonIcon,
  ChatBubbleOutline as ReplyCountIcon,
  AssignmentTurnedIn as ResolveIcon,
  Archive as CloseIcon,
  PlayArrow as InProgressIcon,
} from '@mui/icons-material';
import { formatDistanceToNow, parseISO } from 'date-fns';
import api, { extractData } from '../../utils/api';
import { useAuthStore } from '../../stores/authStore';

interface Ticket {
  id: string;
  ticketNumber: string;
  subject: string;
  description: string;
  category: string;
  priority: string;
  status: string;
  userId: string | null;
  organizationId: number;
  assignedTo: string | null;
  requesterEmail: string | null;
  requesterName: string | null;
  isExternal: boolean;
  createdAt: string;
  updatedAt: string;
  userEmail?: string;
  userName?: string;
  assignedEmail?: string;
  assignedName?: string;
  replyCount?: number;
}

interface TicketsResponse {
  tickets: Ticket[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

const STATUS_COLORS: Record<string, string> = {
  open: '#3b82f6',
  in_progress: '#f59e0b',
  resolved: '#10b981',
  closed: '#6b7280',
};

const STATUS_LABELS: Record<string, string> = {
  open: 'Open',
  in_progress: 'In Progress',
  resolved: 'Resolved',
  closed: 'Closed',
};

const PRIORITY_COLORS: Record<string, string> = {
  urgent: '#ef4444',
  high: '#f97316',
  medium: '#f59e0b',
  low: '#22c55e',
};

const CATEGORIES = ['general', 'billing', 'technical', 'account'];

const getPriorityIcon = (priority: string) => {
  switch (priority) {
    case 'urgent': return <UrgentIcon sx={{ fontSize: 14 }} />;
    case 'high': return <HighIcon sx={{ fontSize: 14 }} />;
    case 'medium': return <MediumIcon sx={{ fontSize: 14 }} />;
    case 'low': return <LowIcon sx={{ fontSize: 14 }} />;
    default: return null;
  }
};

// Quick-view sidebar tabs
type ViewFilter = 'all' | 'my' | 'unassigned' | 'open' | 'in_progress' | 'resolved' | 'closed';

const VIEWS: { id: ViewFilter; label: string; color?: string }[] = [
  { id: 'all', label: 'All Tickets' },
  { id: 'my', label: 'My Tickets' },
  { id: 'unassigned', label: 'Unassigned' },
  { id: 'open', label: 'Open', color: STATUS_COLORS.open },
  { id: 'in_progress', label: 'In Progress', color: STATUS_COLORS.in_progress },
  { id: 'resolved', label: 'Resolved', color: STATUS_COLORS.resolved },
  { id: 'closed', label: 'Closed', color: STATUS_COLORS.closed },
];

const HelpdeskTickets: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const basePath = isHelpdeskMode() ? '/support' : '/helpdesk';
  const user = useAuthStore((s) => s.user);
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';
  const ticketsPath = isAdmin ? '/support/tickets' : '/support/my-tickets';

  // State
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<'list' | 'card'>('list');
  const [activeView, setActiveView] = useState<ViewFilter>('all');

  // Filters
  const [searchInput, setSearchInput] = useState(searchParams.get('search') || '');
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Create dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [newSubject, setNewSubject] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [newCategory, setNewCategory] = useState('general');
  const [newPriority, setNewPriority] = useState('medium');
  const [creating, setCreating] = useState(false);

  // Bulk actions
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkWorking, setBulkWorking] = useState(false);

  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });

  // Auto-open create dialog from sidebar "New Ticket" button
  useEffect(() => {
    if (searchParams.get('new') === 'true') {
      setCreateOpen(true);
    }
  }, [searchParams]);

  const loadTickets = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, any> = { page, limit: 25 };
      if (search) params.search = search;

      // Apply view filter
      if (activeView === 'open') params.status = 'open';
      else if (activeView === 'in_progress') params.status = 'in_progress';
      else if (activeView === 'resolved') params.status = 'resolved';
      else if (activeView === 'closed') params.status = 'closed';
      else if (activeView === 'unassigned') params.assignedTo = 'none';
      else if (statusFilter) params.status = statusFilter;

      if (priorityFilter) params.priority = priorityFilter;
      if (categoryFilter) params.category = categoryFilter;

      // "My Tickets" view for admin — filter by their user ID
      const path = (activeView === 'my' && isAdmin) ? '/support/my-tickets' : ticketsPath;

      const res = await api.get(path, { params });
      const data: TicketsResponse = extractData(res);
      setTickets(data.tickets || []);
      setTotal(data.total || 0);
      setTotalPages(data.totalPages || 1);
    } catch {
      setSnackbar({ open: true, message: 'Failed to load tickets', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [page, search, activeView, statusFilter, priorityFilter, categoryFilter, ticketsPath, isAdmin]);

  useEffect(() => { loadTickets(); }, [loadTickets]);

  const handleSearch = () => {
    setSearch(searchInput);
    setPage(1);
  };

  const handleViewChange = (v: ViewFilter) => {
    setActiveView(v);
    setStatusFilter('');
    setPage(1);
  };

  const handleCreate = async () => {
    if (!newSubject.trim() || !newDescription.trim()) return;
    setCreating(true);
    try {
      await api.post('/support/tickets', {
        subject: newSubject,
        description: newDescription,
        category: newCategory,
        priority: newPriority,
      });
      setCreateOpen(false);
      setNewSubject('');
      setNewDescription('');
      setNewCategory('general');
      setNewPriority('medium');
      setSnackbar({ open: true, message: 'Ticket created successfully', severity: 'success' });
      loadTickets();
    } catch {
      setSnackbar({ open: true, message: 'Failed to create ticket', severity: 'error' });
    } finally {
      setCreating(false);
    }
  };

  const clearFilters = () => {
    setSearch('');
    setSearchInput('');
    setStatusFilter('');
    setPriorityFilter('');
    setCategoryFilter('');
    setActiveView('all');
    setPage(1);
  };

  // Bulk selection helpers
  const toggleSelect = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === tickets.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(tickets.map(t => t.id)));
    }
  };

  const handleBulkStatus = async (status: string) => {
    if (!selected.size) return;
    setBulkWorking(true);
    try {
      await Promise.all([...selected].map(id => api.put(`${ticketsPath}/${id}`, { status })));
      setSelected(new Set());
      setSnackbar({ open: true, message: `${selected.size} ticket(s) updated to ${STATUS_LABELS[status]}`, severity: 'success' });
      loadTickets();
    } catch {
      setSnackbar({ open: true, message: 'Some updates failed', severity: 'error' });
    } finally {
      setBulkWorking(false);
    }
  };

  const activeFilters = [search, statusFilter, priorityFilter, categoryFilter].filter(Boolean).length;

  return (
    <Box sx={{ display: 'flex', height: '100%' }}>
      {/* Views Sidebar */}
      <Box
        sx={{
          width: 200,
          minWidth: 200,
          bgcolor: '#fff',
          borderRight: '1px solid #e5e7eb',
          py: 2,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Typography variant="caption" fontWeight={600} sx={{ px: 2, mb: 1, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          Views
        </Typography>
        {VIEWS.filter(v => isAdmin || !['unassigned'].includes(v.id)).map((view) => (
          <Box
            key={view.id}
            onClick={() => handleViewChange(view.id)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 2,
              py: 0.85,
              cursor: 'pointer',
              fontSize: '0.85rem',
              fontWeight: activeView === view.id ? 600 : 400,
              color: activeView === view.id ? '#3b82f6' : '#4b5563',
              bgcolor: activeView === view.id ? '#eff6ff' : 'transparent',
              borderRight: activeView === view.id ? '3px solid #3b82f6' : '3px solid transparent',
              transition: 'all 0.1s',
              '&:hover': { bgcolor: activeView === view.id ? '#eff6ff' : '#f9fafb' },
            }}
          >
            {view.color && <DotIcon sx={{ fontSize: 8, color: view.color }} />}
            <span>{view.label}</span>
          </Box>
        ))}
      </Box>

      {/* Main List Area */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Bulk Action Bar — appears when tickets are selected */}
        {isAdmin && selected.size > 0 && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              px: 2.5,
              py: 1,
              bgcolor: alpha('#3b82f6', 0.06),
              borderBottom: '1px solid',
              borderColor: alpha('#3b82f6', 0.2),
            }}
          >
            <Checkbox
              size="small"
              indeterminate={selected.size > 0 && selected.size < tickets.length}
              checked={selected.size === tickets.length && tickets.length > 0}
              onChange={toggleSelectAll}
              sx={{ p: 0.5, color: '#3b82f6' }}
            />
            <Typography variant="body2" fontWeight={600} sx={{ color: '#1e40af' }}>
              {selected.size} selected
            </Typography>
            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
            <Button
              size="small"
              startIcon={bulkWorking ? <CircularProgress size={12} sx={{ color: '#3b82f6' }} /> : <InProgressIcon sx={{ fontSize: 14 }} />}
              onClick={() => handleBulkStatus('in_progress')}
              disabled={bulkWorking}
              sx={{ textTransform: 'none', fontSize: '0.78rem', color: '#f59e0b', fontWeight: 600, borderRadius: '6px', minWidth: 0, px: 1.25 }}
            >
              In Progress
            </Button>
            <Button
              size="small"
              startIcon={<ResolveIcon sx={{ fontSize: 14 }} />}
              onClick={() => handleBulkStatus('resolved')}
              disabled={bulkWorking}
              sx={{ textTransform: 'none', fontSize: '0.78rem', color: '#10b981', fontWeight: 600, borderRadius: '6px', minWidth: 0, px: 1.25 }}
            >
              Resolve
            </Button>
            <Button
              size="small"
              startIcon={<CloseIcon sx={{ fontSize: 14 }} />}
              onClick={() => handleBulkStatus('closed')}
              disabled={bulkWorking}
              sx={{ textTransform: 'none', fontSize: '0.78rem', color: '#6b7280', fontWeight: 600, borderRadius: '6px', minWidth: 0, px: 1.25 }}
            >
              Close
            </Button>
            <Box sx={{ flex: 1 }} />
            <Button
              size="small"
              onClick={() => setSelected(new Set())}
              sx={{ textTransform: 'none', fontSize: '0.75rem', color: '#6b7280' }}
            >
              Clear
            </Button>
          </Box>
        )}

        {/* Toolbar */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            px: 2.5,
            py: 1.5,
            bgcolor: '#fff',
            borderBottom: '1px solid #e5e7eb',
          }}
        >
          {/* Search */}
          <TextField
            size="small"
            placeholder="Search tickets..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: '#9ca3af' }} />
                </InputAdornment>
              ),
              sx: { fontSize: '0.85rem', borderRadius: '8px' },
            }}
            sx={{ width: 280 }}
          />

          {/* Filter Toggle */}
          <Tooltip title="Filters">
            <IconButton size="small" onClick={() => setShowFilters(!showFilters)} sx={{ color: showFilters || activeFilters ? '#3b82f6' : '#6b7280' }}>
              <FilterIcon fontSize="small" />
              {activeFilters > 0 && (
                <Box sx={{ position: 'absolute', top: 2, right: 2, width: 8, height: 8, borderRadius: '50%', bgcolor: '#ef4444' }} />
              )}
            </IconButton>
          </Tooltip>

          <Tooltip title="Refresh">
            <IconButton size="small" onClick={loadTickets} sx={{ color: '#6b7280' }}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Box sx={{ flex: 1 }} />

          {/* View Toggle */}
          <Box sx={{ display: 'flex', bgcolor: '#f3f4f6', borderRadius: '6px', p: 0.25 }}>
            <IconButton size="small" onClick={() => setViewMode('list')} sx={{ color: viewMode === 'list' ? '#3b82f6' : '#9ca3af', bgcolor: viewMode === 'list' ? '#fff' : 'transparent', borderRadius: '4px', boxShadow: viewMode === 'list' ? '0 1px 2px rgba(0,0,0,0.1)' : 'none' }}>
              <ListViewIcon sx={{ fontSize: 18 }} />
            </IconButton>
            <IconButton size="small" onClick={() => setViewMode('card')} sx={{ color: viewMode === 'card' ? '#3b82f6' : '#9ca3af', bgcolor: viewMode === 'card' ? '#fff' : 'transparent', borderRadius: '4px', boxShadow: viewMode === 'card' ? '0 1px 2px rgba(0,0,0,0.1)' : 'none' }}>
              <CardViewIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>

          <Typography variant="body2" sx={{ color: '#9ca3af', mx: 1 }}>{total} tickets</Typography>

          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => setCreateOpen(true)}
            sx={{
              bgcolor: '#3b82f6',
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: '8px',
              boxShadow: 'none',
              '&:hover': { bgcolor: '#2563eb', boxShadow: 'none' },
            }}
          >
            New Ticket
          </Button>
        </Box>

        {/* Filter Bar */}
        {showFilters && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              px: 2.5,
              py: 1,
              bgcolor: '#fafbfc',
              borderBottom: '1px solid #e5e7eb',
            }}
          >
            <FormControl size="small" sx={{ minWidth: 130 }}>
              <InputLabel sx={{ fontSize: '0.85rem' }}>Status</InputLabel>
              <Select value={statusFilter} label="Status" onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }} sx={{ fontSize: '0.85rem', borderRadius: '8px' }}>
                <MenuItem value="">All</MenuItem>
                {Object.entries(STATUS_LABELS).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 130 }}>
              <InputLabel sx={{ fontSize: '0.85rem' }}>Priority</InputLabel>
              <Select value={priorityFilter} label="Priority" onChange={(e) => { setPriorityFilter(e.target.value); setPage(1); }} sx={{ fontSize: '0.85rem', borderRadius: '8px' }}>
                <MenuItem value="">All</MenuItem>
                {['urgent', 'high', 'medium', 'low'].map(p => <MenuItem key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 130 }}>
              <InputLabel sx={{ fontSize: '0.85rem' }}>Category</InputLabel>
              <Select value={categoryFilter} label="Category" onChange={(e) => { setCategoryFilter(e.target.value); setPage(1); }} sx={{ fontSize: '0.85rem', borderRadius: '8px' }}>
                <MenuItem value="">All</MenuItem>
                {CATEGORIES.map(c => <MenuItem key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</MenuItem>)}
              </Select>
            </FormControl>
            {activeFilters > 0 && (
              <Button size="small" onClick={clearFilters} sx={{ textTransform: 'none', color: '#6b7280', fontSize: '0.8rem' }}>
                Clear filters
              </Button>
            )}
          </Box>
        )}

        {/* Content */}
        <Box sx={{ flex: 1, overflow: 'auto', p: viewMode === 'card' ? 2.5 : 0 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}>
              <CircularProgress size={36} />
            </Box>
          ) : tickets.length === 0 ? (
            <Box sx={{ textAlign: 'center', pt: 8 }}>
              <Typography variant="h6" sx={{ color: '#9ca3af', mb: 1 }}>No tickets found</Typography>
              <Typography variant="body2" sx={{ color: '#d1d5db', mb: 3 }}>
                {activeFilters > 0 ? 'Try adjusting your filters' : 'Create your first ticket to get started'}
              </Typography>
              <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)} sx={{ textTransform: 'none', borderRadius: '8px' }}>
                Create Ticket
              </Button>
            </Box>
          ) : viewMode === 'list' ? (
            /* List View */
            <Box>
              {/* Table Header */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: isAdmin
                    ? '36px 90px 1fr 100px 90px 80px 120px 120px 100px'
                    : '90px 1fr 100px 90px 80px 100px',
                  px: 2,
                  py: 1,
                  bgcolor: '#f9fafb',
                  borderBottom: '1px solid #e5e7eb',
                  position: 'sticky',
                  top: 0,
                  zIndex: 1,
                  alignItems: 'center',
                }}
              >
                {isAdmin && (
                  <Checkbox
                    size="small"
                    indeterminate={selected.size > 0 && selected.size < tickets.length}
                    checked={selected.size === tickets.length && tickets.length > 0}
                    onChange={toggleSelectAll}
                    sx={{ p: 0, color: '#9ca3af' }}
                  />
                )}
                {[
                  'Ticket #', 'Subject', 'Status', 'Priority', 'Replies',
                  ...(isAdmin ? ['Requester', 'Assigned'] : []),
                  'Created',
                ].map((h) => (
                  <Typography key={h} variant="caption" fontWeight={600} sx={{ color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.67rem' }}>
                    {h}
                  </Typography>
                ))}
              </Box>
              {/* Rows */}
              {tickets.map((t) => (
                <Box
                  key={t.id}
                  onClick={() => navigate(`${basePath}/tickets/${t.id}`)}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: isAdmin
                      ? '36px 90px 1fr 100px 90px 80px 120px 120px 100px'
                      : '90px 1fr 100px 90px 80px 100px',
                    px: 2,
                    py: 1.1,
                    cursor: 'pointer',
                    borderBottom: '1px solid #f3f4f6',
                    alignItems: 'center',
                    transition: 'background 0.1s',
                    bgcolor: selected.has(t.id) ? alpha('#3b82f6', 0.04) : 'transparent',
                    '&:hover': { bgcolor: selected.has(t.id) ? alpha('#3b82f6', 0.06) : '#f9fafb' },
                  }}
                >
                  {isAdmin && (
                    <Checkbox
                      size="small"
                      checked={selected.has(t.id)}
                      onClick={(e) => toggleSelect(t.id, e)}
                      sx={{ p: 0.5, color: '#9ca3af', '&.Mui-checked': { color: '#3b82f6' } }}
                    />
                  )}
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', color: '#6366f1', fontWeight: 600, fontSize: '0.78rem' }}>
                    {t.ticketNumber}
                  </Typography>
                  <Box sx={{ overflow: 'hidden', pr: 2 }}>
                    <Typography variant="body2" sx={{ color: '#1f2937', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.85rem' }}>
                      {t.subject}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.2 }}>
                      <Typography variant="caption" sx={{ color: '#9ca3af', textTransform: 'capitalize', fontSize: '0.7rem' }}>{t.category}</Typography>
                      {t.isExternal && (
                        <Chip label="External" size="small" sx={{ height: 14, fontSize: '0.6rem', bgcolor: '#ecfeff', color: '#0891b2', px: 0.25 }} />
                      )}
                    </Box>
                  </Box>
                  <Chip
                    size="small"
                    label={STATUS_LABELS[t.status] || t.status}
                    sx={{
                      height: 22,
                      fontSize: '0.68rem',
                      fontWeight: 600,
                      bgcolor: `${STATUS_COLORS[t.status] || '#9e9e9e'}15`,
                      color: STATUS_COLORS[t.status] || '#9e9e9e',
                      width: 'fit-content',
                    }}
                  />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ color: PRIORITY_COLORS[t.priority] || '#9e9e9e', display: 'flex' }}>
                      {getPriorityIcon(t.priority)}
                    </Box>
                    <Typography variant="caption" sx={{ color: PRIORITY_COLORS[t.priority] || '#6b7280', fontWeight: 500, fontSize: '0.75rem' }}>
                      {t.priority?.charAt(0).toUpperCase() + t.priority?.slice(1)}
                    </Typography>
                  </Box>
                  {/* Reply count */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <ReplyCountIcon sx={{ fontSize: 13, color: t.replyCount ? '#6b7280' : '#e5e7eb' }} />
                    <Typography variant="caption" sx={{ color: t.replyCount ? '#6b7280' : '#d1d5db', fontSize: '0.75rem', fontWeight: t.replyCount ? 600 : 400 }}>
                      {t.replyCount ?? 0}
                    </Typography>
                  </Box>
                  {isAdmin && (
                    <>
                      <Typography variant="caption" sx={{ color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.75rem' }}>
                        {t.requesterName || t.userName || t.requesterEmail || t.userEmail || '—'}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {t.assignedName || t.assignedEmail ? (
                          <Chip
                            size="small"
                            label={t.assignedName || t.assignedEmail}
                            sx={{ height: 18, fontSize: '0.65rem', maxWidth: 110, overflow: 'hidden', bgcolor: '#ede9fe', color: '#6366f1', fontWeight: 500 }}
                          />
                        ) : (
                          <Typography variant="caption" sx={{ color: '#ef4444', fontSize: '0.72rem', fontStyle: 'italic' }}>Unassigned</Typography>
                        )}
                      </Box>
                    </>
                  )}
                  <Typography variant="caption" sx={{ color: '#9ca3af', fontSize: '0.72rem' }}>
                    {formatDistanceToNow(parseISO(t.createdAt), { addSuffix: true })}
                  </Typography>
                </Box>
              ))}
            </Box>
          ) : (
            /* Card View */
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 2 }}>
              {tickets.map((t) => (
                <Paper
                  key={t.id}
                  elevation={0}
                  onClick={() => navigate(`${basePath}/tickets/${t.id}`)}
                  sx={{
                    p: 2,
                    borderRadius: '10px',
                    border: '1px solid #e5e7eb',
                    cursor: 'pointer',
                    transition: 'all 0.15s',
                    '&:hover': { borderColor: '#3b82f6', boxShadow: '0 2px 8px rgba(59,130,246,0.1)' },
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Typography variant="caption" sx={{ fontFamily: 'monospace', color: '#6366f1', fontWeight: 600 }}>
                      {t.ticketNumber}
                    </Typography>
                    <Chip
                      size="small"
                      label={STATUS_LABELS[t.status] || t.status}
                      sx={{
                        height: 20,
                        fontSize: '0.65rem',
                        fontWeight: 600,
                        bgcolor: `${STATUS_COLORS[t.status] || '#9e9e9e'}15`,
                        color: STATUS_COLORS[t.status] || '#9e9e9e',
                      }}
                    />
                  </Box>
                  <Typography variant="body2" fontWeight={600} sx={{ color: '#1f2937', mb: 0.75, lineHeight: 1.4 }}>
                    {t.subject}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: PRIORITY_COLORS[t.priority] }}>
                      {getPriorityIcon(t.priority)}
                      <Typography variant="caption" fontWeight={500} sx={{ fontSize: '0.72rem' }}>
                        {t.priority?.charAt(0).toUpperCase() + t.priority?.slice(1)}
                      </Typography>
                    </Box>
                    <Typography variant="caption" sx={{ color: '#d1d5db' }}>•</Typography>
                    <Typography variant="caption" sx={{ color: '#9ca3af', textTransform: 'capitalize' }}>{t.category}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <PersonIcon sx={{ fontSize: 14, color: '#9ca3af' }} />
                      <Typography variant="caption" sx={{ color: '#6b7280', fontSize: '0.72rem' }}>
                        {t.requesterName || t.userName || t.requesterEmail || t.userEmail || '—'}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <ClockIcon sx={{ fontSize: 12, color: '#d1d5db' }} />
                      <Typography variant="caption" sx={{ color: '#9ca3af', fontSize: '0.7rem' }}>
                        {formatDistanceToNow(parseISO(t.createdAt), { addSuffix: true })}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              ))}
            </Box>
          )}
        </Box>

        {/* Pagination */}
        {totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 1.5, borderTop: '1px solid #e5e7eb', bgcolor: '#fff' }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(_, p) => setPage(p)}
              size="small"
              shape="rounded"
            />
          </Box>
        )}
      </Box>

      {/* Create Ticket Dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '12px' } }}>
        <DialogTitle sx={{ fontWeight: 600, pb: 1 }}>Create New Ticket</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
          <TextField
            label="Subject"
            fullWidth
            required
            value={newSubject}
            onChange={(e) => setNewSubject(e.target.value)}
            size="small"
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
          />
          <TextField
            label="Description"
            fullWidth
            required
            multiline
            rows={4}
            value={newDescription}
            onChange={(e) => setNewDescription(e.target.value)}
            size="small"
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
          />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <FormControl size="small" fullWidth>
              <InputLabel>Category</InputLabel>
              <Select value={newCategory} label="Category" onChange={(e) => setNewCategory(e.target.value)} sx={{ borderRadius: '8px' }}>
                {CATEGORIES.map(c => <MenuItem key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small" fullWidth>
              <InputLabel>Priority</InputLabel>
              <Select value={newPriority} label="Priority" onChange={(e) => setNewPriority(e.target.value)} sx={{ borderRadius: '8px' }}>
                {['low', 'medium', 'high', 'urgent'].map(p => <MenuItem key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</MenuItem>)}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setCreateOpen(false)} sx={{ textTransform: 'none', color: '#6b7280' }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={creating || !newSubject.trim() || !newDescription.trim()}
            sx={{ textTransform: 'none', bgcolor: '#3b82f6', borderRadius: '8px', boxShadow: 'none', fontWeight: 600, '&:hover': { bgcolor: '#2563eb' } }}
          >
            {creating ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Create Ticket'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} variant="filled" sx={{ borderRadius: '8px' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default HelpdeskTickets;
